import { useCallback, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Saves a trail to Bike Paths as a background job and polls its progress, so long
// trails never hit a browser/proxy timeout. Re-attaches to a save already running.
export function useTrailSave(onDone) {
  const [job, setJob] = useState(null);

  const poll = useCallback(async (jobId) => {
    for (;;) {
      await sleep(2500);
      const { data } = await api.get(`/admin/trails/save-jobs/${jobId}`);
      setJob(data);
      if (data.status !== "running") return data;
    }
  }, []);

  const save = useCallback(async (trailId, bufferMiles) => {
    let jobId;
    try {
      const { data } = await api.post(`/admin/trails/${trailId}/save`, { buffer_miles: Number(bufferMiles) || 1 });
      jobId = data.job_id;
      setJob({ status: "running", step: "Starting…", progress: 2, name: data.name });
    } catch (e) {
      if (e.response?.status === 409) {
        const { data } = await api.get(`/admin/trails/${trailId}/save-job`).catch(() => ({ data: {} }));
        if (data.job?.status === "running") { jobId = data.job.id; setJob(data.job); }
      }
      if (!jobId) { toast.error(apiError(e.response?.data?.detail)); return; }
    }
    try {
      const done = await poll(jobId);
      if (done.status === "done") {
        toast.success(`Saved "${done.result?.name || done.name}" as a DRAFT bike path (${bufferMiles} mi corridor) — review & publish in Bike Paths.`);
        if (onDone) onDone(done.result);
      } else toast.error(done.error || "Saving failed — please try again.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [poll, onDone]);

  return { job, saving: job?.status === "running", save };
}

export function TrailSaveProgress({ job }) {
  if (!job || job.status !== "running") return null;
  return (
    <div className="mt-2 space-y-1" data-testid="trail-save-progress">
      <Progress value={job.progress || 0} className="h-2" />
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span data-testid="trail-save-step">{job.step}</span>
        <span>{job.progress || 0}%</span>
      </div>
      <div className="text-[11px] text-muted-foreground">Long trails can take several minutes. You can keep working — it continues in the background.</div>
    </div>
  );
}
